// ============================================================
// SkyPlus Intelligence Component
// app/components/skyplus/intelligence/intelligence.component.ts
//
// Full-page chat UI. Three columns:
//   - Left rail: chat history (demo: 3 hardcoded threads)
//   - Centre:    chat thread + input
//   - Right rail: dynamic suggested questions (per-claim if a claim is in
//                 context, otherwise generic)
//
// Reads ?q=... from URL so the flip viewer's suggested-asks chips work.
// ============================================================

import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { SectorService } from '../shared/sector/sector.service';
import { IntelligenceService, IntelligenceResponse, SuggestedFollowup, Citation } from './intelligence.service';

interface ChatMessage {
  role:        'user' | 'assistant';
  text:        string;
  citations?:  Citation[];
  suggested?:  SuggestedFollowup[];
  tone?:       'info' | 'warning' | 'success';
  timestamp:   Date;
  pending?:    boolean;
}

interface HistoryThread {
  id:      string;
  label:   string;
  preview: string;
}

@Component({
  selector:    'sp-intelligence',
  templateUrl: './intelligence.component.html',
  styleUrls:   ['./intelligence.component.scss']
})
export class IntelligenceComponent implements OnInit, OnDestroy, AfterViewChecked {

  @ViewChild('threadScroll') threadScroll!: ElementRef<HTMLDivElement>;
  @ViewChild('inputBox')     inputBox!:     ElementRef<HTMLTextAreaElement>;

  messages: ChatMessage[] = [];
  draft = '';
  isSending = false;
  shouldScroll = false;

  // History sidebar — hardcoded for demo. Post-contract this comes from
  // a real /api/intelligence/threads endpoint.
  history: HistoryThread[] = [
    { id: 't1', label: 'Claims missing police abstracts', preview: 'Yesterday' },
    { id: 't2', label: 'Reserves over KES 500k',           preview: '2 days ago' },
    { id: 't3', label: 'Joseph Mwangi claim history',      preview: 'Last week' }
  ];

  // Right-rail suggested questions. These change based on the current
  // context: if the last assistant response cited a claim, we show
  // questions about that claim. Otherwise generic.
  suggestedQuestions: SuggestedFollowup[] = [
    { label: 'List open claims',        question: 'List all open claims' },
    { label: 'Missing docs CLM-00187',  question: 'What\'s missing from CLM-2026-00187?' },
    { label: 'Reserves on MED-3311',    question: 'Total reserve under MED-2025-3311' },
    { label: 'Member history',          question: 'Has Joseph Mwangi filed similar claims before?' }
  ];

  // Current context (claim/policy/doc) shown in right-rail header.
  contextLabel = '';
  contextType  = '';

  private routeSub?: Subscription;

  constructor(
    private route:        ActivatedRoute,
    public  sector:       SectorService,
    private intelligence: IntelligenceService
  ) {}

  ngOnInit(): void {
    // Greeting message
    //const placeholder = this.sector.label('ai.placeholder') || 'Ask about claims, policies, members...';
    this.messages.push({
      role:      'assistant',
      text:      this.greeting(),
      suggested: this.suggestedQuestions.slice(0, 3),
      timestamp: new Date(),
      tone:      'info'
    });

    // If the URL has ?q=..., auto-send that query.
    this.routeSub = this.route.queryParamMap.subscribe(params => {
      const q = params.get('q');
      if (q && q.trim()) {
        this.send(q.trim());
      }
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll && this.threadScroll?.nativeElement) {
      this.threadScroll.nativeElement.scrollTop =
        this.threadScroll.nativeElement.scrollHeight;
      this.shouldScroll = false;
    }
  }

  // ── User actions ────────────────────────────────────────────

  onKeyDown(ev: KeyboardEvent): void {
    if (ev.key === 'Enter' && !ev.shiftKey) {
      ev.preventDefault();
      this.send();
    }
  }

  send(textOverride?: string): void {
    const text = (textOverride ?? this.draft).trim();
    if (!text || this.isSending) return;

    // Push user message
    this.messages.push({ role: 'user', text, timestamp: new Date() });
    this.draft = '';

    // Push pending placeholder
    const pending: ChatMessage = {
      role: 'assistant', text: '', pending: true, timestamp: new Date()
    };
    this.messages.push(pending);
    this.isSending = true;
    this.shouldScroll = true;

    this.intelligence.ask(text).subscribe({
      next: (resp: IntelligenceResponse) => {
        const idx = this.messages.indexOf(pending);
        if (idx >= 0) {
          this.messages[idx] = {
            role:      'assistant',
            text:      resp.answer || '',
            citations: resp.citations || [],
            suggested: resp.suggested || [],
            tone:      resp.tone || 'info',
            timestamp: new Date()
          };
        }
        this.refreshContextFromResponse(resp);
        this.isSending = false;
        this.shouldScroll = true;
      },
      error: (err) => {
        const idx = this.messages.indexOf(pending);
        if (idx >= 0) {
          this.messages[idx] = {
            role:      'assistant',
            text:      'I had trouble reaching the intelligence service. ' +
                       (err?.status === 403
                         ? 'AI chat is not enabled for this tenant.'
                         : 'Please try again.'),
            tone:      'warning',
            timestamp: new Date()
          };
        }
        this.isSending = false;
        this.shouldScroll = true;
      }
    });
  }

  pickSuggestion(s: SuggestedFollowup): void {
    this.send(s.question);
  }

  openHistoryThread(t: HistoryThread): void {
    // Demo: clear thread and seed with a hardcoded conversation per id.
    // Post-contract: GET /api/intelligence/threads/:id
    this.messages = [{
      role: 'assistant',
      text: `Loading thread "${t.label}"...`,
      timestamp: new Date()
    }];
    setTimeout(() => {
      this.messages = [];
      // Trigger the right question to repopulate
      const seed: Record<string, string> = {
        t1: 'Which claims have missing documents?',
        t2: 'Total reserve under MOT-2025-8812',
        t3: 'Has Joseph Mwangi filed similar claims before?'
      };
      this.send(seed[t.id] || 'List all open claims');
    }, 200);
  }

  newThread(): void {
    this.messages = [];
    this.contextLabel = '';
    this.contextType  = '';
    this.ngOnInit();
  }

  // ── Helpers ────────────────────────────────────────────────

  /**
   * Update right-rail context + suggestions based on citations in
   * the latest response. If the response mentions a claim, the
   * right rail switches to that claim's context.
   */
  private refreshContextFromResponse(resp: IntelligenceResponse): void {
    if (!resp.citations || !resp.citations.length) return;

    const claim = resp.citations.find(c => c.type === 'claim');
    if (claim) {
      this.contextLabel = claim.name;
      this.contextType  = 'Claim';
      this.suggestedQuestions = [
        { label: `Summarize ${claim.name}`,          question: `Summarize ${claim.name}` },
        { label: `What's missing?`,                  question: `What's missing from ${claim.name}?` },
        { label: `Documents in ${claim.name}`,       question: `Show documents in ${claim.name}` },
        { label: `Member history`,                   question: `Has the claimant filed similar claims before?` }
      ];
      return;
    }

    const policy = resp.citations.find(c => c.type === 'policy');
    if (policy) {
      this.contextLabel = policy.name;
      this.contextType  = 'Policy';
      this.suggestedQuestions = [
        { label: `Claims under ${policy.name}`,      question: `Show claims under ${policy.name}` },
        { label: `Total exposure`,                   question: `Total reserve under ${policy.name}` }
      ];
      return;
    }
  }

  private greeting(): string {
    const productName = this.sector.brand?.productName || 'SkyPlus';
    return `Hello! I'm the assistant for ${productName}. I can search across your claims, policies, members, and documents. Ask me anything — or try one of these:`;
  }

  // Template helpers
  trackByIdx(i: number) { return i; }
  formatTime(d: Date)   { return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
}
